<script setup lang="ts">
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { statistics } = useReviewStatistics();

const averageRating = computed(() => {
  return statistics?.value?.data?.averageRating?.toFixed(2) || '....';
});
const { ratingData, bekanntData, personalAdviceData, getProductCustomSettings } = useProductPageCustomData()

onMounted(() => {
  nextTick(async () => {
    await getProductCustomSettings();
  });
});


</script>

<template>
  <div class="footer-column collapse-item mb-6 md:mb-0">
    <h4 class="h4 text-lg md:text-xl lg:text-2xl 3xl:h3 text-primary mb-2 lg:mb-6">{{ $t("footer.customerService") }}</h4>

    <div class="flex items-end justify-between">
      <div class="flex flex-col gap-4 3xl:gap-6 text-base 3xl:text-md">
        <div class="contact-wrap leading-7">
          <div class="contact-item block">
            {{ $t("companyDetails.phone") }}: <a :href="`tel:${$t('companyDetails.contactNumber')}`" class="font-bold hover:text-primary">{{ $t("companyDetails.contactNumber") }}</a>
          </div>
          <div class="contact-item block">
            {{ $t("companyDetails.emailText") }}:
            <NuxtLink :to="`mailto:${$t('companyDetails.email')}`" class="font-bold hover:text-primary">
              {{ $t("companyDetails.email") }}
            </NuxtLink>
          </div>
          <NuxtLink :to="formatLink('/Informationen/Kontakt/')" class="block underline hover:text-primary">{{ $t('companyDetails.contactForm') }}</NuxtLink>
        </div>
        <div class="working-hours leading-7">
          <span class="block">{{ $t('companyDetails.timingsMonday') }}</span>
          <span class="block">{{ $t('companyDetails.timingsSaturday') }}</span>
        </div>
        <div class="rating-widget hidden sm:block mt-2">
          <div class="flex gap-4">
            <NuxtLink :aria-label="$t('footerLinks.sslSecureLabel')" :to="$t('footerLinks.sslSecure')" class="hover:underline">
              <img
                src="/assets/images/ssl-secure.png"
                :alt="$t('footer.sslSecure')"
                class="h-[70px]"
                height="70"
                width="70"
                loading="lazy" 
              />
            </NuxtLink>
            <NuxtLink :aria-label="$t('footerLinks.trustedShopsLabel')" :to="$t('footerLinks.trustedShops')" class="hover:underline">
              <NuxtImg
                :src="ratingData?.ratingLogo?.url"
                :alt="$t('footer.trustedShops')"
                class="h-[70px]"
                height="70"
                width="70"
                loading="lazy" 
                format="webp"
              />
            </NuxtLink>
          </div>
          <div class="flex items-center gap-2 mt-4">
            <NuxtLink
              v-if="averageRating"
              :aria-label="$t('headerLinks.ratingLinkLabel')"
              :to="$t('headerLinks.ratingLink')"
              class="text-md font-medium hover:underline"
            >
              <span :aria-label="$t('header.ariaLabelRatingText')">{{ averageRating }}/</span>
              <span class="text-gray-500 font-light">5.00</span>
            </NuxtLink>
            <div class="rating-stars flex gap-x-0 sm:gap-x-[4px] text-[13px] sm:text-base text-rating-yellow relative -mt-[4px] sm:mt-0" aria-hidden="true">
              <template v-for="starIndex in 5" :key="starIndex">
                    <div class="relative inline-block">
                      <!-- Background star (always gray) -->
                      <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-gray-300"></i>
                      <!-- Foreground star (filled portion) -->
                      <div 
                        class="absolute top-0 left-0 bottom-0 h-full overflow-hidden"
                        :style="{
                          width: averageRating ? `${Math.min(100, Math.max(0, ((parseFloat(averageRating) - (starIndex - 1)) * 100))) }%` : '0%'
                        }">
                        <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-rating-yellow"></i>
                     </div>
                    </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="flex sm:hidden flex-col gap-4 3xl:gap-6 text-base 3xl:text-md">
        <div class="rating-widget mt-2">
          <div class="flex flex-row items-center gap-2">
            <img
              src="/assets/images/ssl-secure.png"
              :alt="$t('footer.sslSecure')"
              class="h-[50px] sm:h-[70px] w-auto"
              height="70"
              width="70"
              loading="lazy" />
              <NuxtImg
                :src="ratingData?.ratingLogo?.url"
                :alt="$t('footer.trustedShops')"
                class="h-[50px] sm:h-[70px] w-auto"
                height="70"
                width="70"
                loading="lazy" 
                format="webp"
              />
          </div>
          <div class="flex flex-col items-center gap-1 mt-3">
            <NuxtLink
              v-if="averageRating"
              :aria-label="$t('headerLinks.ratingLinkLabel')"
              :to="$t('headerLinks.ratingLink')"
              class="text-sm font-medium hover:underline"
            >
              <span :aria-label="$t('header.ariaLabelRatingText')">{{ averageRating }}/</span>
              <span class="text-gray-500 font-light">5.00</span>
            </NuxtLink>
            <div class="rating-stars flex gap-x-0 sm:gap-x-[4px] text-[13px] sm:text-base text-rating-yellow relative -mt-[4px] sm:mt-0" aria-hidden="true">
              <template v-for="starIndex in 5" :key="starIndex">
                    <div class="relative inline-block">
                      <!-- Background star (always gray) -->
                      <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-gray-300"></i>
                      <!-- Foreground star (filled portion) -->
                      <div 
                        class="absolute top-0 left-0 bottom-0 h-full overflow-hidden"
                        :style="{
                          width: averageRating ? `${Math.min(100, Math.max(0, ((parseFloat(averageRating) - (starIndex - 1)) * 100))) }%` : '0%'
                        }">
                        <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-rating-yellow"></i>
                     </div>
                    </div>
                  </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>